import detectIncognito from 'detectincognitojs';
import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported, type Messaging } from 'firebase/messaging';
import { isNil } from 'lodash-es';
import { BehaviorSubject, filter, type Observable } from 'rxjs';
import type { PushNotificationEvent } from '@/models/events/push-notification-event';
import { setFcmDeviceToken, setIsPrivate } from '@/stores/device';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import { createLogger } from '@/utils/logger';

export function createFirebaseManager() {
  return new FirebaseManager();
}

export default class FirebaseManager {
  readonly #instance: FirebaseApp;
  readonly #logger = createLogger('Firebase');
  readonly #event$ = new BehaviorSubject<PushNotificationEvent | null>(null);
  #messagingWorker: ServiceWorkerRegistration | null = null;

  #isInitialized = false;

  constructor() {
    this.#instance = initializeApp(window.__CONFIG?.config ?? {});
  }

  get isInitialized() {
    return this.#isInitialized;
  }

  get messagingInstance(): Messaging | undefined {
    if (this.#instance) {
      return getMessaging(this.#instance);
    }

    return undefined;
  }

  get event$(): Observable<PushNotificationEvent> {
    return this.#event$.asObservable().pipe(filter((event) => !isNil(event)));
  }

  async revokeDeviceToken() {
    if (this.#instance) {
      await deleteToken(getMessaging(this.#instance));
    }
  }

  async shouldRequestPermission(permission: NotificationPermission = 'default') {
    if (!(await isSupported())) {
      this.#logger.warn('Notifications is not supported.');
      return false;
    }

    const { isPrivate } = await detectIncognito();
    setIsPrivate(isPrivate);

    if (isPrivate) {
      this.#logger.warn('Notifications are not supported in private or incognito mode.');
      return false;
    }

    const currentPermission = Notification.permission || (await Notification.requestPermission());
    if (currentPermission === permission) {
      return true;
    }
    this.#logger.info(`Notification permission "${currentPermission}" does not match the requested "${permission}".`);

    return false;
  }

  async initialize(): Promise<string> {
    if (!('serviceWorker' in navigator)) return '';
    if (BUILD_EMBED_TYPE === 'embed-native') return '';

    // Use the existing root service worker registration (Serwist) instead of registering a dedicated FCM SW
    const registration = await navigator.serviceWorker.ready;
    this.#messagingWorker = registration;

    if (!navigator.serviceWorker.controller) {
      this.#logger.info('Service worker is ready but not yet controlling this page. Waiting for controllerchange...');
      await new Promise<void>((resolve) => {
        const onChange = () => {
          navigator.serviceWorker.removeEventListener('controllerchange', onChange);
          resolve();
        };
        navigator.serviceWorker.addEventListener('controllerchange', onChange);
      });
    }

    await registration.update();

    const token = await this.#initializeDeviceToken();

    this.#isInitialized = true;

    return token;
  }

  async #initializeDeviceToken() {
    const vapidKey = window.__CONFIG?.vapId;
    let token = '';

    if (!vapidKey) {
      this.#logger.warn('No vapid key provided.');
      return token;
    }

    try {
      if (this.#messagingWorker) {
        token = await getToken(getMessaging(this.#instance), {
          vapidKey,
          serviceWorkerRegistration: this.#messagingWorker,
        });
      }

      if (token) {
        this.#logger.info('retrieve token successful.');
        setFcmDeviceToken(token);
      } else {
        this.#logger.warn('No registration token available. Request permission to generate one.');
      }
    } catch (e) {
      this.#logger.error('An error occurred while retrieving token.', e);
    }

    return token;
  }
}
